/** @typedef {'en' | 'so' | 'ar'} Lang */

/** @type {Record<Lang, Record<string, unknown>>} */
export const translations = {
  en: {
    nav: {
      brand: 'Give with purpose',
      about: 'About',
      causes: 'Areas',
      resources: 'Resources',
      donate: 'Donate',
      totals: 'Totals',
      contribute: 'Contribute',
      projects: 'Vote',
      contact: 'Contact',
      login: 'Login',
      logout: 'Logout',
      admin: 'Admin',
      langLabel: 'Language',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      langEn: 'English',
      langSo: 'Soomaali',
      langAr: 'العربية',
    },
    hero: {
      eyebrow: 'Charity & National Capital Fund',
      title: 'Give with purpose',
      lead:
        'Every contribution is split per our Terms: 40% to charitable relief for the most needy in Somaliland, and 60% to the National Capital Project Fund—held in escrow and allocated through democratic community voting.',
      cta: 'Donation accounts',
      videoTitle: 'Maalin iyo Habeen introduction video',
    },
    about: {
      title: 'About us',
      sub: 'Maalin iyo Habeen',
      body1:
        'Maalin iyo Habeen is a registered charitable organization in the Republic of Somaliland. We raise funds through a dual model: 40% of proceeds support humanitarian relief—mental health, disability, funerals, mosques, water, and food aid—while 60% flows to the National Capital Project Fund for Committee-governed investment in Somaliland’s natural resources.',
      body2:
        'Charitable funds are prioritized for the most needy communities. Capital funds are held in an independent escrow account and allocated through democratic voting by the public and investors. Maalin iyo Habeen has no decision-making authority over Capital Fund spending.',
      visionTitle: 'Our vision',
      visionText:
        'A Somali society where vulnerable people access relief with dignity, and where national wealth is invested transparently for the benefit of all through community governance.',
      missionTitle: 'Our mission',
      missionText:
        'To mobilize voluntary giving, steward charitable relief with integrity, and facilitate democratic allocation of the National Capital Project Fund in partnership with an independent Committee of elders and experts.',
    },
    contact: {
      title: 'Contact',
      sub: 'Reach out for questions about our work or your donation.',
      emailLabel: 'Email',
      emailValue: 'info@maaliniyohabeen.org',
      phoneLabel: 'Phone',
      phoneValue: '+252 63 4808147',
      addressLabel: 'Address',
      addressValue: 'Hargeisa, Somaliland',
    },
    contactPage: {
      eyebrow: 'Get in touch',
      title: 'Contact us',
      lead:
        'Questions about donations, programs, or partnerships? Reach us by email, phone, or address below—or send a message with the form.',
    },
    contactForm: {
      formTitle: 'Send a message',
      nameLabel: 'Name',
      namePlaceholder: 'Your full name',
      emailLabel: 'Your email',
      emailPlaceholder: 'you@example.com',
      messageLabel: 'Message',
      messagePlaceholder: 'How can we help?',
      submit: 'Send message',
      helper: 'Opens your email app to send your message to us.',
      mailSubject: 'Message from Maalin iyo Habeen website',
      mailBodyIntro: 'This message was sent from the contact form on the website.',
    },
    sectorsSection: {
      title: 'Charitable relief (40%)',
      sub:
        'The charitable portion of your contribution supports these humanitarian programs. A 5% administrative fee is deducted from this portion; the remaining 95% goes directly to programs.',
    },
    resources: {
      title: 'National Capital Project Fund sectors (60%)',
      sub:
        'Capital funds may be allocated to projects in these sectors through democratic voting overseen by an independent Committee. Explore Somaliland’s natural wealth below.',
      categories: {
        livestock: 'Livestock & value-chain',
        agriculture: 'Agriculture & horticulture',
        frankincense: 'Frankincense & myrrh',
        fish: 'Fisheries',
        metals: 'Metals',
        minerals: 'Industrial minerals',
        miningMinerals: 'Mining & minerals',
        oilGas: 'Oil & gas exploration',
        coal: 'Coal',
      },
      fishGroups: {
        tuna: 'Tuna',
        billfish: 'Billfish',
        mackerel: 'Mackerel',
        reefFish: 'Reef fish',
        sharksRays: 'Sharks & rays',
      },
      items: {
        camels: 'Camels',
        goats: 'Goats',
        sheep: 'Sheep',
        cattle: 'Cattle',
        boswelliaCarterii: 'Boswellia carterii (Synonym: Boswellia sacra)',
        boswelliaFrereana: 'Boswellia frereana',
        boswelliaOcculta: 'Boswellia occulta',
        boswelliaRivae: 'Boswellia rivae',
        myrrh: 'Myrrh',
        yellowfin: 'Yellowfin tuna',
        skipjack: 'Skipjack tuna',
        albacore: 'Albacore tuna',
        marlin: 'Marlin (e.g. Blue Marlin)',
        swordfish: 'Swordfish',
        kingMackerel: 'King mackerel',
        spanishMackerel: 'Spanish mackerel',
        snapper: 'Snapper',
        groupers: 'Groupers',
        emperors: 'Emperors',
        goatFish: 'Goat fish',
        sweetlips: 'Sweet lips',
        sharks: 'Sharks',
        rays: 'Rays',
        lithium: 'Lithium',
        tantalum: 'Tantalum',
        niobium: 'Niobium',
        titanium: 'Titanium',
        rareEarthElements: 'Rare earth elements',
        tin: 'Tin',
        copper: 'Copper',
        lead: 'Lead',
        zinc: 'Zinc',
        tungsten: 'Tungsten',
        molybdenum: 'Molybdenum',
        manganese: 'Manganese',
        ironOre: 'Iron ore',
        gypsumAnhydrite: 'High-purity gypsum and anhydrite',
        limestone: 'Limestone',
        marble: 'Marble',
        dolomite: 'Dolomite',
        feldspar: 'Feldspar',
        mica: 'Mica',
        sorghum: 'Sorghum',
        maize: 'Maize',
        wheat: 'Wheat',
        sesame: 'Sesame',
        fruits: 'Fruits',
        vegetables: 'Vegetables',
        silicaSand: 'Silica sand',
        heavyMineralSands: 'Heavy mineral sands',
        onshoreExploration: 'Onshore exploration',
        offshoreExploration: 'Offshore exploration',
        coalExploration: 'Coal exploration',
        coalDevelopment: 'Coal development',
      },
    },
    sectors: {
      madness: {
        title: 'Madness',
        description:
          'Support care, treatment, and dignity for people living with mental illness—medicine, shelter, and community outreach.',
      },
      disability: {
        title: 'Disability',
        description:
          'Help meet the needs of persons with disabilities—mobility aids, accessibility, livelihoods, and inclusion.',
      },
      funerals: {
        title: 'Funerals',
        description:
          'Assist families who cannot afford a dignified burial—janazah costs, transport, and related hardship.',
      },
      mosquesMadrasas: {
        title: 'Mosques and madrasas',
        description:
          'Build, repair, and sustain mosques and madrasas for prayer, Quranic education, and community gatherings.',
      },
      waterWells: {
        title: 'Water wells',
        description:
          'Fund wells and clean water so households spend less time fetching water and face fewer waterborne illnesses.',
      },
      food: {
        title: 'Food',
        description:
          'Provide food aid for families facing hunger—emergency meals, staples for orphans and widows, and Ramadan support.',
      },
    },
    accountsSection: {
      title: 'Donation accounts',
      sub: 'Send your contribution via Zaad, Edahab, or bank transfer. Tap copy and paste the number in your banking or wallet app.',
    },
    accounts: {
      mobileMoney: 'Mobile money',
      accountNumber: 'Account number',
      copy: 'Copy',
      copied: 'Copied',
    },
    auth: {
      loginTitle: 'Management login',
      loginSub: 'Sign in to manage donations and payment methods.',
      email: 'Email',
      password: 'Password',
      signIn: 'Sign in',
      signingIn: 'Signing in…',
      loginError: 'Invalid email or password',
      demoTitle: 'Demo account',
      demoEmail: 'Email',
      demoPassword: 'Password',
    },
    voting: {
      title: 'Vote for your preferred payment method',
      sub: 'Help us understand which donation channel our community prefers. One vote per visitor.',
      vote: 'Vote',
      yourVote: 'Your vote',
      thankYou: 'Thank you for voting!',
      alreadyVoted: 'You have already voted.',
      error: 'Could not record your vote. Please try again.',
    },
    projectVoting: {
      title: 'Vote for a project',
      sub: 'Tell us which cause matters most to you. Your vote helps us prioritize community projects. One vote per visitor.',
      vote: 'Vote for this project',
      yourVote: 'Your choice',
      votes: 'votes',
      thankYou: 'Thank you — your vote has been recorded!',
      alreadyVoted: 'You have already voted for a project.',
      error: 'Could not record your vote. Please try again.',
    },
    contributions: {
      totalsTitle: 'Community totals',
      totalsSub:
        'Approved contributions under our Terms & Conditions: net charitable relief (after 5% admin fee) and National Capital Project Fund balances.',
      totalCharitableNet: 'Charitable relief (net)',
      totalCapitalFund: 'National Capital Project Fund',
      totalCharitableHint: '95% of the 40% charitable portion, after admin fee',
      totalCapitalHint: '60% held for Committee-governed allocation',
      totalDonations: 'Charitable relief (gross)',
      totalInvestments: 'National Capital Project Fund',
      currencyNote: 'USD equivalent',
      formTitle: 'Record your contribution',
      formSub:
        'Enter the total amount you paid via Zaad, Edahab, or bank transfer. Per our Terms, 40% supports charitable relief and 60% goes to the National Capital Project Fund escrow.',
      name: 'Full name',
      namePlaceholder: 'Your name',
      whatsapp: 'WhatsApp phone',
      whatsappPlaceholder: '+252 63 0000000',
      totalAmount: 'Total amount paid',
      totalAmountRequired: 'Enter the total amount you paid.',
      splitTitle: 'Your payment is split as',
      charitableGross: 'Charitable relief',
      adminFee: 'Administrative fee (5% of charitable portion)',
      netCharitable: 'Net to charitable programs',
      capitalFund: 'National Capital Project Fund',
      splitNote:
        'Capital funds are deposited in an independent escrow account and allocated through democratic voting. Maalin iyo Habeen has no authority over Capital Fund spending.',
      donationAmount: 'Charitable relief',
      investmentAmount: 'National Capital Project Fund',
      charitableProgram: 'Charitable program preference (40%)',
      capitalFundSector: 'Capital Fund sector preference (60%)',
      donationProjectVote: 'Charitable program (40%)',
      investmentProjectVote: 'Capital Fund sector (60%)',
      selectProject: 'Select a project',
      donationProjectRequired: 'Select a charitable program for the 40% portion.',
      investmentProjectRequired: 'Select a Capital Fund sector for the 60% portion.',
      paymentAccount: 'Account for payment',
      selectPaymentAccount: 'Select payment account',
      paymentAccountRequired: 'Select the account you paid to.',
      paymentSectionTitle: 'Payment details',
      loadingOptions: 'Loading payment methods and projects…',
      optionsLoadError: 'Could not load payment methods or projects. Check your connection and try again.',
      retryLoad: 'Try again',
      ageConfirm: 'I confirm I am at least 18 years of age.',
      ageRequired: 'You must confirm you are at least 18 years old.',
      termsConfirmPrefix: 'I have read and agree to the',
      termsConfirmLink: 'Terms & Conditions',
      termsRequired: 'You must accept the Terms & Conditions.',
      refundNotice:
        'Donations are generally non-refundable once processed. Refund requests may be considered within 7 days for duplicate or erroneous transactions. No refund is possible once Capital Funds are released from escrow.',
      riskDisclaimer:
        'The Capital Fund is not a guaranteed return vehicle. Investment involves risk and past performance does not indicate future results.',
      formHint:
        'Project selections indicate your preference at submission. Final Capital Fund allocation is decided through democratic voting overseen by the independent Committee. Submissions are reviewed before appearing in public totals.',
      submit: 'Submit for review',
      submitting: 'Submitting…',
      submitSuccess: 'Thank you! Your submission is pending review. Totals will update once approved.',
      submitError: 'Could not submit. Please try again.',
    },
    admin: {
      title: 'Management System',
      welcome: 'Welcome',
      backToSite: 'Back to site',
      logout: 'Logout',
      navAria: 'Admin navigation',
      dashboard: 'Dashboard',
      donations: 'Donations',
      projects: 'Projects',
      methods: 'Payment methods',
      loading: 'Loading…',
      totalDonations: 'Recorded donations',
      paymentMethods: 'Payment methods',
      totalVotes: 'Community votes',
      manageDonations: 'Manage donations',
      manageMethods: 'Manage methods',
      addDonation: 'Add donation',
      editDonation: 'Edit donation',
      addMethod: 'Add payment method',
      editMethod: 'Edit payment method',
      donorName: 'Donor name',
      amount: 'Amount',
      sector: 'Cause area',
      method: 'Payment method',
      notes: 'Notes',
      date: 'Date',
      actions: 'Actions',
      add: 'Add',
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      delete: 'Delete',
      confirmDelete: 'Delete this record?',
      noDonations: 'No donations recorded yet.',
      none: 'None',
      methodId: 'Method ID',
      methodLabel: 'Label',
      methodValue: 'Account / number',
      hintKey: 'Type',
      logoFile: 'Logo filename',
      active: 'Active',
      yes: 'Yes',
      no: 'No',
      votes: 'Votes',
      portalTag: 'Management Portal',
      menuSection: 'Main menu',
      viewSite: 'View public site',
      roleAdmin: 'Administrator',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      dashboardDesc: 'Overview of donations, payment methods, and community votes.',
      donationsDesc: 'Record and manage incoming donations across all cause areas.',
      methodsDesc: 'Configure payment channels shown on the public donate page.',
      donationsList: 'All donations',
      methodsList: 'All payment methods',
      quickActions: 'Quick actions',
      manageDonationsDesc: 'Add, edit, or remove donation records.',
      manageMethodsDesc: 'Update accounts, logos, and vote tallies.',
      manageProjects: 'Manage projects',
      manageProjectsDesc: 'Add causes the public can vote to prioritize.',
      addProject: 'Add project',
      editProject: 'Edit project',
      projectTitle: 'Project title',
      projectDescription: 'Description',
      projectsDesc: 'Manage cause-area projects that visitors vote for on the public site.',
      projectsFormHint: 'Pick a cause area, then adjust the title and description. Each area can have one project.',
      selectSector: 'Select cause area',
      projectSectorRequired: 'Please select a cause area for the project.',
      projectSaveError: 'Could not save the project. Make sure the API server is running.',
      projectsLoadError: 'Could not load projects. Run npm start to launch the API and website together.',
      saving: 'Saving…',
      sectorTaken: 'A project already exists for this cause area',
      projectsList: 'All projects',
      noProjects: 'No projects yet. Add one for the community to vote on.',
      totalProjects: 'Active projects',
      projectVotes: 'Project votes',
      totalAmount: 'Total recorded',
      donationCount: 'Donations',
      unspecified: 'Unspecified',
      kpiDonationsSub: 'Entries in the ledger',
      kpiProjectsSub: 'Open for voting',
      kpiProjectVotesSub: 'Community priorities',
      kpiMethodsSub: 'Active channels',
      kpiMethodVotesSub: 'Payment preferences',
      chartDonationsBySector: 'Donations by cause',
      chartDonationsBySectorSub: 'Distribution across programme areas',
      chartDonationsTrend: 'Donations over time',
      chartDonationsTrendSub: 'Monthly volume and recorded amounts',
      chartProjectVotes: 'Project votes',
      chartProjectVotesSub: 'Which causes the community prioritizes',
      chartMethodVotes: 'Payment method votes',
      chartMethodVotesSub: 'Preferred donation channels',
      noChartData: 'No data yet — activity will appear here.',
      dashboardLoadError: 'Could not load dashboard data.',
      recentDonations: 'Recent donations',
      projectLeaderboard: 'Project voting leaderboard',
      methodLeaderboard: 'Payment method leaderboard',
      viewAll: 'View all',
      contributions: 'Submissions',
      contributionsDesc: 'Review public donation and investment submissions. Approve to add amounts to the website totals.',
      submissionsList: 'Contribution submissions',
      noSubmissions: 'No submissions in this category.',
      status: 'Status',
      statusPending: 'Pending',
      statusApproved: 'Approved',
      statusRejected: 'Rejected',
      statusAll: 'All',
      approve: 'Approve',
      reject: 'Reject',
      pendingSubmissions: 'Pending submissions',
      kpiPendingSub: 'Awaiting review',
      manageContributions: 'Review submissions',
      manageContributionsDesc: 'Approve donations and investments for the public totals.',
      publicTotalDonations: 'Public donation total',
      publicTotalInvestments: 'Public investment total',
      submissionReport: 'Submission report',
      submissionReportDesc: 'Summary, charts, and full export of all donation and investment submissions.',
      reportLoadError: 'Could not load submission report.',
      reportGenerated: 'Report generated',
      reportTotalSubmissions: 'Total submissions',
      reportDonationSubmitted: 'Donations submitted',
      reportInvestmentSubmitted: 'Investments submitted',
      reportStatusBreakdown: 'Status breakdown',
      reportAmountsByStatus: 'Amounts by status',
      reportApproved: 'Approved',
      reportPending: 'Pending',
      reportMonthlyTrend: 'Monthly submission trend',
      reportDetailTable: 'Submission detail',
      reportRecords: 'records',
      reportReviewed: 'Reviewed',
      exportCsv: 'Export CSV',
      printReport: 'Print report',
    },
    footer: {
      thanks:
        'Thank you for your generosity. Please keep your transfer receipt for your records.',
      aboutText:
        'Maalin iyo Habeen is a registered charity in Somaliland. Contributions are split: 40% charitable relief and 60% National Capital Project Fund—governed by an independent Committee through democratic voting.',
      menuHeading: 'Explore',
      menuAria: 'Site sections',
      legalHeading: 'Legal',
      legalAria: 'Legal links',
      contactHeading: 'Contact',
      socialHeading: 'Follow us',
      socialAria: 'Social media links',
      socialFacebook: 'Facebook',
      socialInstagram: 'Instagram',
      socialX: 'X (Twitter)',
      socialYoutube: 'YouTube',
      socialWhatsapp: 'WhatsApp',
      terms: 'Terms & Conditions',
      privacy: 'Privacy Policy',
      rights: 'All rights reserved.',
    },
    legal: {
      backToHome: 'Back to home',
    },
    terms: {
      notice:
        'The binding legal text is in English. If anything in another language differs, the English version prevails.',
    },
    termsGate: {
      title: 'Terms & Conditions',
      lead:
        'Before using Maalin iyo Habeen, please read and accept our Terms & Conditions. This includes the 40/60 fund split, a 5% administrative fee on the charitable portion, National Capital Project Fund escrow governance, democratic voting, and your rights as a donor or investor.',
      checkboxPrefix: 'I have read and agree to the',
      termsLink: 'Terms & Conditions',
      accept: 'Accept and continue',
      decline: 'Decline',
      declinedMessage:
        'You must accept the Terms & Conditions to use this website. Please review them and accept to continue.',
    },
    privacy: {
      title: 'Privacy Policy',
      notice:
        'The binding legal text is in English in our Terms & Conditions (Section 7). If anything here differs, the English Terms prevail.',
      s71: 'We collect personal information (name, contact details, payment information) solely for processing donations, communicating with you, and administering the voting process.',
      s72: 'We do not share, sell, or trade your personal data with third parties for marketing purposes.',
      s73intro: 'Your information may be shared with:',
      s73a: 'Payment processors to complete transactions',
      s73b: 'The Committee and auditors for verification purposes',
      s73c: 'Regulatory authorities if required by law',
      s74: 'We take reasonable technical and organizational measures to protect your data.',
      s75: 'You have the right to request access, correction, or deletion of your data by contacting us at info@maaliniyohabeen.org.',
    },
  },
  so: {
    nav: {
      brand: 'Ku bixi ujeedo leh',
      about: 'Ku saabsan',
      causes: 'Meelaha',
      resources: 'Khayraadka',
      donate: 'Deeq bixi',
      totals: 'Wadarta',
      contribute: 'Ka qayb qaado',
      projects: 'Cod',
      contact: 'Xiriir',
      login: 'Gal',
      logout: 'Ka bax',
      admin: 'Maamul',
      langLabel: 'Luqadda',
      openMenu: 'Fur liiska',
      closeMenu: 'Xir liiska',
      langEn: 'English',
      langSo: 'Soomaali',
      langAr: 'العربية',
    },
    hero: {
      eyebrow: 'Samafal & Sanduuqa Raasamaalka Qaranka',
      title: 'Ku bixi ujeedo leh',
      lead:
        'Deeq kasta waxaa loo qaybiyaa sida Shuruudaha: 40% gargaarka samafalka ee dadka ugu baahan Somaliland, 60% Sanduuqa Mashruuca Raasamaalka Qaranka—oo escrow ku jira oo loo qoondeeyo codbixin dimuqraadi ah.',
      cta: 'Xisaabaadka deeqda',
      videoTitle: 'Fiidiyowga hordhaca Maalin iyo Habeen',
    },
    about: {
      title: 'Ku saabsan',
      sub: 'Maalin iyo Habeen',
      body1:
        'Maalin iyo Habeen waa urur samafal oo diiwaan gashan Jamhuuriyadda Somaliland. Waxaan soo ururinnaa lacag qaab laba-geesood ah: 40% gargaarka bani\'aadamnimo, 60% Sanduuqa Mashruuca Raasamaalka Qaranka oo Guddiga madax-bannaan maamulo.',
      body2:
        'Lacagta samafalka waxaa mudnaanta la siiyaa bulshooyinka ugu baahan. Lacagta raasamaalka waxaa lagu hayaa escrow madax-bannaan waxaana loo qoondeeyaa codbixin dimuqraadi ah. Maalin iyo Habeen ma laha awood go\'aan qaadis ah oo ku saabsan Sanduuqa Raasamaalka.',
      visionTitle: 'Aragtidayada',
      visionText:
        'Bulsho Soomaaliyeed oo dadka nugul ay ku helaan gargaar sharaf leh, hantida qarankuna si hufan loogu maalgeliyo faa\'iidada dhammaan.',
      missionTitle: 'Himiladayada',
      missionText:
        'In la soo ururiyo deeqda iskaa wax u qabso, la maamulo gargaarka samafalka si daacad ah, lana fududeeyo qoondeynta dimuqraadi ah ee Sanduuqa Mashruuca Raasamaalka.',
    },
    contact: {
      title: 'Xiriir',
      sub: 'Nala soo xiriir su\'aalaha shaqada ama deeqdaada.',
      emailLabel: 'Iimayl',
      emailValue: 'info@maaliniyohabeen.org',
      phoneLabel: 'Taleefan',
      phoneValue: '+252 63 4808147',
      addressLabel: 'Cinwaanka',
      addressValue: 'Hargeysa, Somaliland',
    },
    contactPage: {
      eyebrow: 'Nala soo xiriir',
      title: 'Nala soo xiriir',
      lead:
        'Su\'aalaha deeqda, barnaamijyada, ama iskaashiga? Nala soo xiriir iimayl, taleefan, ama cinwaanka—ama dir fariin qoraalka.',
    },
    contactForm: {
      formTitle: 'Dir fariin',
      nameLabel: 'Magaca',
      namePlaceholder: 'Magacaaga oo dhan',
      emailLabel: 'Iimaylkaaga',
      emailPlaceholder: 'you@example.com',
      messageLabel: 'Fariinta',
      messagePlaceholder: 'Sidee baan kuu caawin karnaa?',
      submit: 'Dir fariinta',
      helper: 'Waxay furaysaa iimaylkaaga si aad noogu dirto fariinta.',
      mailSubject: 'Fariin bogga Maalin iyo Habeen',
      mailBodyIntro: 'Fariintan waxaa laga diray foomka xiriirka bogga.',
    },
    sectorsSection: {
      title: 'Meelaha deeqda',
      sub:
        'Dooro meesha sadaqadu ugu faa\'iideysato. Dhammaan kanaalada hoose waxay qaataan deeq loogu talagalay ujeeddooyinkan.',
    },
    resources: {
      title: 'Khayraadka Somaliland',
      sub:
        'Baadh hodantinimada dabiiciga ah ee Somaliland — xoolaha, beeraha, fooxa, kalluunka, biraha, iyo macdanta.',
      categories: {
        livestock: 'Xoolaha',
        agriculture: 'Beeraha',
        frankincense: 'Fooxa & marmar',
        fish: 'Kalluunka & badeecadaha badda',
        metals: 'Biraha',
        minerals: 'Macdanta warshadaha',
      },
      fishGroups: {
        tuna: 'Tuunada',
        billfish: 'Kalluunka billfish',
        mackerel: 'Mackerel',
        reefFish: 'Kalluunka shimbiraaha',
        sharksRays: 'Sharks & rays',
      },
      items: {
        camels: 'Geela',
        goats: 'Ri\'yaha',
        sheep: 'Idaha',
        cattle: 'Lo\'da',
        boswelliaCarterii: 'Boswellia carterii (Synonym: Boswellia sacra)',
        boswelliaFrereana: 'Boswellia frereana',
        boswelliaOcculta: 'Boswellia occulta',
        boswelliaRivae: 'Boswellia rivae',
        myrrh: 'Marmar (Myrrh)',
        yellowfin: 'Yellowfin tuna',
        skipjack: 'Skipjack tuna',
        albacore: 'Albacore tuna',
        marlin: 'Marlin (tusaale Blue Marlin)',
        swordfish: 'Swordfish',
        kingMackerel: 'King mackerel',
        spanishMackerel: 'Spanish mackerel',
        snapper: 'Snapper',
        groupers: 'Groupers',
        emperors: 'Emperors',
        goatFish: 'Goat fish',
        sweetlips: 'Sweet lips',
        sharks: 'Sharks',
        rays: 'Rays',
        lithium: 'Lithium',
        tantalum: 'Tantalum',
        niobium: 'Niobium',
        titanium: 'Titanium',
        rareEarthElements: 'Rare earth elements',
        tin: 'Tin',
        copper: 'Copper',
        lead: 'Lead',
        zinc: 'Zinc',
        tungsten: 'Tungsten',
        molybdenum: 'Molybdenum',
        manganese: 'Manganese',
        ironOre: 'Iron ore',
        gypsumAnhydrite: 'Gypsum iyo anhydrite daahir ah',
        limestone: 'Limestone',
        marble: 'Marble',
        dolomite: 'Dolomite',
        feldspar: 'Feldspar',
        mica: 'Mica',
        sorghum: 'Masaggo',
        maize: 'Galley',
        wheat: 'Qamadi',
        sesame: 'Sisinta',
        fruits: 'Miraha',
        vegetables: 'Khudaarta',
      },
    },
    sectors: {
      madness: {
        title: 'Waali',
        description:
          'Taageer daaweyn, daawo, iyo sharafo dadka qaba cudurka maskaxda—daawo, hoy, iyo wacyigelin bulshada.',
      },
      disability: {
        title: 'Naafonimada',
        description:
          'Ka caawi baahiyaha dadka naafada ah—qalab socod, helitaanka, nolol maalgelin, iyo ka mid noqoshada.',
      },
      funerals: {
        title: 'Aaska',
        description:
          'Gargaar qoysaska aan awoodin kharashka aaska sharaf leh—kharashka janazada, gaadiidka, iyo dhibaatooyinka kale.',
      },
      mosquesMadrasas: {
        title: 'Masaajidda & madrasaha',
        description:
          'Dhiso, dayactir, oo ilaali masaajidda iyo madrasaha salaadda, barashada Quraanka, iyo kulanka bulshada.',
      },
      waterWells: {
        title: 'Ceelasha biyaha',
        description:
          'Maalgeli ceelasha iyo biyo nadiif ah si qoysaska ay u yareeyaan waqtiga biyo-raadinta iyo cudurrada biyaha.',
      },
      food: {
        title: 'Cuntada',
        description:
          'Gaarsi cunto-gargaar qoysaska gaajaysan—cunto degdeg ah, raashinka agoonka iyo carmalka, iyo taageerada Ramadaanka.',
      },
    },
    accountsSection: {
      title: 'Xisaabaadka deeqda',
      sub: 'U dir deeqdaada Zaad, Edahab, ama bangiga. Riix nuqul oo ku dheji lambarka barnaamijkaaga.',
    },
    accounts: {
      mobileMoney: 'Lacag caddayn',
      accountNumber: 'Lambarka xisaabta',
      copy: 'Nuqul',
      copied: 'La nuqulay',
    },
    auth: {
      loginTitle: 'Galitaanka maamulka',
      loginSub: 'Gal si aad u maamusho deeqaha iyo hababka lacag bixinta.',
      email: 'Iimaylka',
      password: 'Furaha sirta',
      signIn: 'Gal',
      signingIn: 'Waa la galayaa…',
      loginError: 'Iimayl ama furaha sirta waa khalad',
      demoTitle: 'Akoonka tijaabada',
      demoEmail: 'Iimaylka',
      demoPassword: 'Furaha sirta',
    },
    voting: {
      title: 'Codso habka lacag bixinta aad doorbidayso',
      sub: 'Naga caawi inaan fahanno habka deeqda ee bulshadu doorbidayso. Hal cod qofkiiba.',
      vote: 'Cod',
      yourVote: 'Codbixintaada',
      thankYou: 'Waad ku mahadsan tahay codbixintaada!',
      alreadyVoted: 'Horay ayaad u codeysay.',
      error: 'Codbixinta lama diiwaangelin. Fadlan isku day mar kale.',
    },
    projectVoting: {
      title: 'Codso mashruuc',
      sub: 'Noo sheeg ujeedada adiga kuu muhiimsan. Codbixintaadu waxay naga caawisaa inaan mudnaanta siinno mashaariicda bulshada. Hal cod qofkiiba.',
      vote: 'U codee mashruucan',
      yourVote: 'Doorashadaada',
      votes: 'cod',
      thankYou: 'Waad ku mahadsan tahay — codbixintaada waa la diiwaangeliyay!',
      alreadyVoted: 'Horay ayaad u codeysay mashruuc.',
      error: 'Codbixinta lama diiwaangelin. Fadlan isku day mar kale.',
    },
    contributions: {
      totalsTitle: 'Wadarta bulshada',
      totalsSub:
        'Deeqaha la ansixiyay sida Shuruudaha: gargaarka samafalka (kadib 5% khidmadda maamulka) iyo Sanduuqa Mashruuca Raasamaalka Qaranka.',
      totalCharitableNet: 'Gargaarka samafalka (safi)',
      totalCapitalFund: 'Sanduuqa Mashruuca Raasamaalka Qaranka',
      totalCharitableHint: '95% qaybta 40% ee samafalka, kadib khidmadda maamulka',
      totalCapitalHint: '60% oo loo hayo qoondeynta Guddiga',
      totalDonations: 'Gargaarka samafalka (guud)',
      totalInvestments: 'Sanduuqa Mashruuca Raasamaalka Qaranka',
      currencyNote: 'USD',
      formTitle: 'Diiwaangeli deeqdaada',
      formSub:
        'Geli wadarta lacagta aad bixisay Zaad, Edahab, ama wareejinta bangiga. Sida Shuruudaha: 40% samafal, 60% Sanduuqa Raasamaalka Qaranka.',
      name: 'Magaca buuxa',
      namePlaceholder: 'Magacaaga',
      whatsapp: 'WhatsApp',
      whatsappPlaceholder: '+252 63 0000000',
      totalAmount: 'Wadarta lacagta la bixiyay',
      totalAmountRequired: 'Geli wadarta lacagta aad bixisay.',
      splitTitle: 'Lacagtaada waa loo qaybiyaa sidan',
      charitableGross: 'Gargaarka samafalka',
      adminFee: 'Khidmadda maamulka (5% qaybta samafalka)',
      netCharitable: 'Safi u socda barnaamijyada samafalka',
      capitalFund: 'Sanduuqa Mashruuca Raasamaalka Qaranka',
      splitNote:
        'Lacagta raasamaalka waxaa lagu hayaa escrow madax-bannaan waxaana loo qoondeeyaa codbixin dimuqraadi ah.',
      donationAmount: 'Gargaarka samafalka',
      investmentAmount: 'Sanduuqa Mashruuca Raasamaalka Qaranka',
      charitableProgram: 'Doorashada barnaamijka samafalka (40%)',
      capitalFundSector: 'Doorashada qaybta Sanduuqa Raasamaalka (60%)',
      donationProjectVote: 'Barnaamijka samafalka (40%)',
      investmentProjectVote: 'Qaybta Sanduuqa Raasamaalka (60%)',
      selectProject: 'Dooro mashruuc',
      donationProjectRequired: 'Dooro barnaamij samafal qaybta 40%.',
      investmentProjectRequired: 'Dooro qayb Sanduuqa Raasamaalka qaybta 60%.',
      paymentAccount: 'Xisaabta lacag bixinta',
      selectPaymentAccount: 'Dooro xisaabta lacag bixinta',
      paymentAccountRequired: 'Dooro xisaabta aad ku bixisay.',
      paymentSectionTitle: 'Faahfaahinta lacag bixinta',
      loadingOptions: 'Waa la soo rarayaa hababka lacag bixinta iyo mashaariicda…',
      optionsLoadError: 'Lama soo rarin hababka lacag bixinta ama mashaariicda. Hubi xiriirka oo isku day mar kale.',
      retryLoad: 'Isku day mar kale',
      ageConfirm: 'Waxaan xaqiijinayaa inaan ka weynahay 18 sano.',
      ageRequired: 'Waa inaad xaqiijisaa inaad ka weyn tahay 18 sano.',
      termsConfirmPrefix: 'Waan akhriyay oo waan aqbalay',
      termsConfirmLink: 'Shuruudaha & Xeerarka',
      termsRequired: 'Waa inaad aqbashaa Shuruudaha & Xeerarka.',
      refundNotice:
        'Deeqaha guud ahaan lama celin karo marka la farsameeyo. Codsiyada celinta waxaa laga yaabaa in la tixgeliyo 7 maalmood gudahood.',
      riskDisclaimer:
        'Sanduuqa Raasamaalka ma aha damaanad faa\'iido. Maalgashigu wuxuu leeyahay halis.',
      formHint:
        'Doorashada mashruucdu waa doorbid marka la gudbinayo. Qoondeynta ugu dambaysa waxaa go\'aamiya codbixin dimuqraadi ah oo Guddi madax-bannaan maamulo.',
      submit: 'U gudbi dib u eegista',
      submitting: 'Waa la gudbinayaa…',
      submitSuccess: 'Waad ku mahadsan tahay! Gudbintaadu waxay sugaysaa ansixinta.',
      submitError: 'Lama gudbin. Fadlan isku day mar kale.',
    },
    admin: {
      title: 'Nidaamka Maamulka',
      welcome: 'Soo dhawoow',
      backToSite: 'Ku noqo bogga',
      logout: 'Ka bax',
      navAria: 'Hagaha maamulka',
      dashboard: 'Dashboard',
      donations: 'Deeqaha',
      projects: 'Mashaariicda',
      methods: 'Hababka lacag bixinta',
      loading: 'Waa la soo rarayaa…',
      totalDonations: 'Deeqaha la diiwaangeliyay',
      paymentMethods: 'Hababka lacag bixinta',
      totalVotes: 'Codbixinta bulshada',
      manageDonations: 'Maamul deeqaha',
      manageMethods: 'Maamul hababka',
      addDonation: 'Ku dar deeq',
      editDonation: 'Wax ka beddel deeqda',
      addMethod: 'Ku dar hab lacag bixin',
      editMethod: 'Wax ka beddel habka',
      donorName: 'Magaca deeq bixiyaha',
      amount: 'Qadarka',
      sector: 'Goobta ujeedada',
      method: 'Habka lacag bixinta',
      notes: 'Qoraallo',
      date: 'Taariikhda',
      actions: 'Ficillo',
      add: 'Ku dar',
      save: 'Kaydi',
      cancel: 'Jooji',
      edit: 'Wax ka beddel',
      delete: 'Tirtir',
      confirmDelete: 'Ma tirtirtaa diiwaankan?',
      noDonations: 'Weli deeq lama diiwaangelin.',
      none: 'Midna',
      methodId: 'Aqoonsiga habka',
      methodLabel: 'Magaca',
      methodValue: 'Xisaabta / lambarka',
      hintKey: 'Nooca',
      logoFile: 'Magaca sawirka',
      active: 'Firfircoon',
      yes: 'Haa',
      no: 'Maya',
      votes: 'Codbixinno',
      portalTag: 'Bogga Maamulka',
      menuSection: 'Liiska ugu weyn',
      viewSite: 'Eeg bogga dadweynaha',
      roleAdmin: 'Maamule',
      openMenu: 'Fur liiska',
      closeMenu: 'Xir liiska',
      dashboardDesc: 'Dulmar guud oo deeqaha, hababka lacag bixinta, iyo codbixinta bulshada.',
      donationsDesc: 'Diiwaangeli oo maamul deeqaha soo galaya dhammaan goobaha ujeedada.',
      methodsDesc: 'Habee kanaalada lacag bixinta ee lagu muujiyo bogga deeqda.',
      donationsList: 'Dhammaan deeqaha',
      methodsList: 'Dhammaan hababka lacag bixinta',
      quickActions: 'Ficillo degdeg ah',
      manageDonationsDesc: 'Ku dar, wax ka beddel, ama tirtir diiwaannada deeqda.',
      manageMethodsDesc: 'Cusbooneysii xisaabaadka, astaamaha, iyo tirada codadka.',
      manageProjects: 'Maamul mashaariicda',
      manageProjectsDesc: 'Ku dar ujeedooyin bulshadu u codeyn karto.',
      addProject: 'Ku dar mashruuc',
      editProject: 'Wax ka beddel mashruuca',
      projectTitle: 'Cinwaanka mashruuca',
      projectDescription: 'Sharaxaad',
      projectsDesc: 'Maamul mashaariicda ujeedooyinka ee booqdayaashu u codeyn karaan bogga dadweynaha.',
      projectsFormHint: 'Dooro goobta ujeedada, ka dibna hagaaji cinwaanka iyo sharaxaadda. Goob kasta waxay yeelan kartaa hal mashruuc.',
      selectSector: 'Dooro goobta ujeedada',
      projectSectorRequired: 'Fadlan dooro goobta ujeedada mashruuca.',
      projectSaveError: 'Mashruuca lama kaydin. Hubi in server-ka API uu socdo.',
      projectsLoadError: 'Mashaariicda lama soo rarin. Ku ordi npm start si API iyo bogga ay wada bilaabmaan.',
      saving: 'Waa la kaydinayaa…',
      sectorTaken: 'Mashruuc horay ayuu uga jiraa goobtan ujeedada',
      projectsList: 'Dhammaan mashaariicda',
      noProjects: 'Weli mashruuc ma jiro. Ku dar mid bulshadu u codeyso.',
      totalProjects: 'Mashaariic firfircoon',
      projectVotes: 'Codbixinta mashaariicda',
      totalAmount: 'Wadarta la diiwaangeliyay',
      donationCount: 'Deeqaha',
      unspecified: 'Aan la cayimin',
      kpiDonationsSub: 'Gelitaanka diiwaanka',
      kpiProjectsSub: 'U furan codbixinta',
      kpiProjectVotesSub: 'Mudnaanta bulshada',
      kpiMethodsSub: 'Kanaallo firfircoon',
      kpiMethodVotesSub: 'Doorbidida lacag bixinta',
      chartDonationsBySector: 'Deeqaha sida ujeedada',
      chartDonationsBySectorSub: 'Qaybinta meelaha barnaamijka',
      chartDonationsTrend: 'Deeqaha waqtiga',
      chartDonationsTrendSub: 'Tirada bishii iyo qadarka la diiwaangeliyay',
      chartProjectVotes: 'Codbixinta mashaariicda',
      chartProjectVotesSub: 'Ujeedooyinka bulshadu mudnaanta siiso',
      chartMethodVotes: 'Codbixinta hababka lacag bixinta',
      chartMethodVotesSub: 'Kanaalada deeqda ee la doorbido',
      noChartData: 'Weli xog ma jirto — waxqabadka halkan ayuu ka muuqan doonaa.',
      dashboardLoadError: 'Xogta dashboard-ka lama soo rarin.',
      recentDonations: 'Deeqaha ugu dambeeyay',
      projectLeaderboard: 'Horyaalka codbixinta mashaariicda',
      methodLeaderboard: 'Horyaalka hababka lacag bixinta',
      viewAll: 'Eeg dhammaan',
      contributions: 'Gudbinta',
      contributionsDesc: 'Dib u eeg gudbinta deeqda iyo maalgashiga ee dadweynaha. Ansixi si qadarku ugu daro wadarta bogga.',
      submissionsList: 'Gudbinta deeqda & maalgashiga',
      noSubmissions: 'Ma jiraan gudbino qaybtan.',
      status: 'Xaalad',
      statusPending: 'Sugitaan',
      statusApproved: 'La ansixiyay',
      statusRejected: 'La diiday',
      statusAll: 'Dhammaan',
      approve: 'Ansixi',
      reject: 'Diid',
      pendingSubmissions: 'Gudbinta sugaysa',
      kpiPendingSub: 'Sugitaan dib u eegis',
      manageContributions: 'Dib u eeg gudbinta',
      manageContributionsDesc: 'Ansixi deeqaha iyo maalgashiyada wadarta dadweynaha.',
      publicTotalDonations: 'Wadarta deeqda dadweynaha',
      publicTotalInvestments: 'Wadarta maalgashiga dadweynaha',
      submissionReport: 'Warbixinta gudbinta',
      submissionReportDesc: 'Soo koobid, jaantusyo, iyo dhoofinta dhammaan gudbinta deeqda iyo maalgashiga.',
      reportLoadError: 'Warbixinta gudbinta lama soo rarin.',
      reportGenerated: 'Warbixinta la sameeyay',
      reportTotalSubmissions: 'Wadarta gudbinta',
      reportDonationSubmitted: 'Deeqaha la gudbiyay',
      reportInvestmentSubmitted: 'Maalgashiyada la gudbiyay',
      reportStatusBreakdown: 'Qaybinta xaaladaha',
      reportAmountsByStatus: 'Qadarka sida xaaladda',
      reportApproved: 'La ansixiyay',
      reportPending: 'Sugitaan',
      reportMonthlyTrend: 'Isbeddelka bishii',
      reportDetailTable: 'Faahfaahinta gudbinta',
      reportRecords: 'diiwaan',
      reportReviewed: 'La eegay',
      exportCsv: 'Dhoof CSV',
      printReport: 'Daabac warbixinta',
    },
    footer: {
      thanks:
        'Waad ku mahadsan tahay deeqdaada. Fadlan hay raashinka lacag bixinta diiwaankaaga.',
      aboutText:
        'Maalin iyo Habeen waa urur samafal oo diiwaan gashan oo u adeegaya bulshooyinka dhulka Soomaalida—barnaamijyo caafimaad maskaxeed, gargaar, iyo horumar.',
      menuHeading: 'Bogagga',
      menuAria: 'Qaybaha bogga',
      legalHeading: 'Sharciga',
      legalAria: 'Xiriiriyada sharciga',
      contactHeading: 'Xiriir',
      socialHeading: 'Nala raac',
      socialAria: 'Xiriiriyada baraha bulshada',
      socialFacebook: 'Facebook',
      socialInstagram: 'Instagram',
      socialX: 'X (Twitter)',
      socialYoutube: 'YouTube',
      socialWhatsapp: 'WhatsApp',
      terms: 'Shuruudaha & Xeerarka',
      privacy: 'Siyaasadda asturnaanta',
      rights: 'Xuquuqda oo dhan way ilaalinayaan.',
    },
    legal: {
      backToHome: 'Ku noqo bogga hore',
    },
    terms: {
      notice:
        'Qoraalka sharciga ah ee rasmiga ah waa Ingiriisi. Haddii luqad kale ay kala duwan tahay, Ingiriisiga ayaa mudnaanta leh.',
    },
    termsGate: {
      title: 'Shuruudaha & Xeerarka',
      lead:
        'Ka hor intaadan isticmaalin Maalin iyo Habeen, fadlan akhri oo aqbal Shuruudaha & Xeerarkayaga. Tan waxaa ku jira qaybinta deeqda, maamulka Sanduuqa Mashruuca Raasamaalka Qaranka, iyo xuquuqdaada deeq-bixiye ama maalgeliye ahaan.',
      checkboxPrefix: 'Waan akhriyay oo waan aqbalay',
      termsLink: 'Shuruudaha & Xeerarka',
      accept: 'Aqbal oo sii wad',
      decline: 'Diid',
      declinedMessage:
        'Waa inaad aqbashaa Shuruudaha & Xeerarka si aad u isticmaasho boggan. Fadlan dib u eeg oo aqbal si aad u sii wadato.',
    },
    privacy: {
      title: 'Siyaasadda asturnaanta',
      notice:
        'Qoraalka sharciga ah ee rasmiga ah waa Ingiriisi ee Shuruudaha (Qaybta 7). Haddii wax kala duwan yihiin, Ingiriisiga ayaa mudnaanta leh.',
      s71: 'Waxaan ururinnaa macluumaadka shakhsiga ah (magac, xiriir, lacag bixinta) si aan u farsameyno deeqaha, kula xiriirno, una maamulno habka codbixinta.',
      s72: 'Kuma iibinno ama kuma wadaagno xogtaada dhinac saddexaad ujeeddo suuqgeyn.',
      s73intro: 'Macluumaadkaaga waxaa lala wadaagi karaa:',
      s73a: 'Habaynta lacag bixinta si loo dhammaystiro macaamilka',
      s73b: 'Guddiga iyo xisaabiyeyaasha xaqiijinta',
      s73c: 'Hay\'adaha sharciga haddii loo baahdo',
      s74: 'Waxaan qaadnaa tallaabooyin farsamo iyo urur si aan u ilaalino xogtaada.',
      s75: 'Waxaad xaq u leedahay inaad codsato gelitaanka, hagaajinta, ama tirtirka xogtaada adigoo nala soo xiriira info@maaliniyohabeen.org.',
    },
  },
  ar: {
    nav: {
      brand: 'Maalin iyo Habeen',
      about: 'من نحن',
      causes: 'المجالات',
      resources: 'الموارد',
      donate: 'تبرع',
      totals: 'الإجماليات',
      contribute: 'ساهم',
      projects: 'صوّت',
      contact: 'اتصل بنا',
      login: 'تسجيل الدخول',
      logout: 'تسجيل الخروج',
      admin: 'الإدارة',
      langLabel: 'اللغة',
      openMenu: 'فتح القائمة',
      closeMenu: 'إغلاق القائمة',
      langEn: 'English',
      langSo: 'Soomaali',
      langAr: 'العربية',
    },
    hero: {
      eyebrow: 'صدقة وخير',
      title: 'تبرّع بعزم',
      lead:
        'اختر القضية التي تهمك. كل تبرع يدعم مشاريع حقيقية على الأرض. استخدم أيًا من الحسابات أدناه للتبرع.',
      cta: 'حسابات التبرع',
      videoTitle: 'فيديو تعريفي عن Maalin iyo Habeen',
    },
    about: {
      title: 'من نحن',
      sub: 'Maalin iyo Habeen',
      body1:
        'نحن منظمة خيرية مسجلة في جمهورية أرض الصومال، وتمتد عملياتنا إلى جمهورية الصومال الفيدرالية. نجمع التبرعات وننفّذ مشاريع في الصحة النفسية، والحماية الاجتماعية، وتنمية المجتمع، والإغاثة الطارئة في أراضي الصومال.',
      body2:
        'نعمل بأمانة وشفافية واحترام لكل مجتمع نخدمه—بغض النظر عن القبيلة أو المنطقة أو الانتماء السياسي.',
      visionTitle: 'رؤيتنا',
      visionText:
        'مجتمع صومالي يستطيع فيه الضعفاء—ليلًا ونهارًا—الحصول على دعم الصحة النفسية والحماية الإنسانية والإغاثة بكرامة وإنصاف وأمل.',
      missionTitle: 'رسالتنا',
      missionText:
        'تعبئة التبرع الطوعي والشراكات الموثوقة لتمويل وتنفيذ مشاريع شفافة ومسؤولة تقوّي المجتمعات وتستجيب للاحتياج الملح في أراضي الصومال.',
    },
    contact: {
      title: 'اتصل بنا',
      sub: 'تواصل معنا لأسئلة حول عملنا أو تبرعك.',
      emailLabel: 'البريد الإلكتروني',
      emailValue: 'info@maaliniyohabeen.org',
      phoneLabel: 'الهاتف',
      phoneValue: '+252 63 4808147',
      addressLabel: 'العنوان',
      addressValue: 'هرجيسا، صوماليلاند',
    },
    contactPage: {
      eyebrow: 'تواصل معنا',
      title: 'اتصل بنا',
      lead:
        'أسئلة حول التبرعات أو البرامج أو الشراكات؟ تواصل عبر البريد أو الهاتف أو العنوان—أو أرسل رسالة عبر النموذج.',
    },
    contactForm: {
      formTitle: 'أرسل رسالة',
      nameLabel: 'الاسم',
      namePlaceholder: 'اسمك الكامل',
      emailLabel: 'بريدك الإلكتروني',
      emailPlaceholder: 'you@example.com',
      messageLabel: 'الرسالة',
      messagePlaceholder: 'كيف يمكننا مساعدتك؟',
      submit: 'إرسال الرسالة',
      helper: 'يفتح تطبيق البريد لإرسال رسالتك إلينا.',
      mailSubject: 'رسالة من موقع Maalin iyo Habeen',
      mailBodyIntro: 'أُرسلت هذه الرسالة من نموذج الاتصال على الموقع.',
    },
    sectorsSection: {
      title: 'مجالات التبرع',
      sub:
        'اختر حيث يصنع صدقتك فرقًا. يمكن لجميع القنوات أدناه استقبال تبرعات مخصصة لهذه الأسباب.',
    },
    resources: {
      title: 'موارد صوماليلاند',
      sub:
        'استكشف الثروات الطبيعية في صوماليلاند — الثروة الحيوانية والزراعة واللبان والأسماك والمعادن.',
      categories: {
        livestock: 'الثروة الحيوانية',
        agriculture: 'الزراعة',
        frankincense: 'اللبان والمر',
        fish: 'الأسماك والمنتجات البحرية',
        metals: 'المعادن',
        minerals: 'المعادن الصناعية',
      },
      fishGroups: {
        tuna: 'التونة',
        billfish: 'أسماك الفاتورة',
        mackerel: 'الماكريل',
        reefFish: 'أسماك الشعاب',
        sharksRays: 'أسماك القرش والشفنين',
      },
      items: {
        camels: 'الإبل',
        goats: 'الماعز',
        sheep: 'الأغنام',
        cattle: 'الأبقار',
        boswelliaCarterii: 'Boswellia carterii (مرادف: Boswellia sacra)',
        boswelliaFrereana: 'Boswellia frereana',
        boswelliaOcculta: 'Boswellia occulta',
        boswelliaRivae: 'Boswellia rivae',
        myrrh: 'المر',
        yellowfin: 'تونة صفراء الزعانف',
        skipjack: 'تونة سكيب جاك',
        albacore: 'تونة البكور',
        marlin: 'مارلين (مثل المارلين الأزرق)',
        swordfish: 'سمك أبو سيف',
        kingMackerel: 'ماكريل ملكي',
        spanishMackerel: 'ماكريل إسباني',
        snapper: 'الناجل (Snapper)',
        groupers: 'أسماك الهامور',
        emperors: 'أسماك الإمبراطور',
        goatFish: 'سمك الماعز',
        sweetlips: 'Sweet lips',
        sharks: 'أسماك القرش',
        rays: 'الشفنين',
        lithium: 'الليثيوم',
        tantalum: 'التنتالوم',
        niobium: 'النيوبيوم',
        titanium: 'التيتانيوم',
        rareEarthElements: 'عناصر الأرض النادرة',
        tin: 'القصدير',
        copper: 'النحاس',
        lead: 'الرصاص',
        zinc: 'الزنك',
        tungsten: 'التنجستن',
        molybdenum: 'الموليبدينوم',
        manganese: 'المنغنيز',
        ironOre: 'خام الحديد',
        gypsumAnhydrite: 'جبس وأنهيدرايت عالي النقاء',
        limestone: 'الحجر الجيري',
        marble: 'الرخام',
        dolomite: 'الدولوميت',
        feldspar: 'الفلسبار',
        mica: 'الميكا',
        sorghum: 'الذرة الرفيعة',
        maize: 'الذرة',
        wheat: 'القمح',
        sesame: 'السمسم',
        fruits: 'الفواكه',
        vegetables: 'الخضروات',
      },
    },
    sectors: {
      madness: {
        title: 'الأمراض النفسية',
        description:
          'دعم الرعاية والعلاج والكرامة لمن يعانون من الأمراض النفسية—دواء ومأوى وتوعية مجتمعية.',
      },
      disability: {
        title: 'ذووا الإعاقة',
        description:
          'تلبية احتياجات ذوي الإعاقة—أجهزة تعويضية، وإتاحة الوصول، وسبل العيش، والاندماج.',
      },
      funerals: {
        title: 'الجنائز',
        description:
          'مساعدة العائلات التي لا تستطيع تحمل تكاليف دفن لائق—تكاليف الجنازة والنقل وغيرها.',
      },
      mosquesMadrasas: {
        title: 'المساجد والمدارس',
        description:
          'بناء وصيانة واستدامة المساجد والمدارس للصلاة وتعليم القرآن والمجتمع.',
      },
      waterWells: {
        title: 'آبار المياه',
        description:
          'تمويل الآبار والمياه النظيفة لتقليل وقت الجلب والأمراض المنقولة بالمياه.',
      },
      food: {
        title: 'الغذاء',
        description:
          'إغاثة غذائية للعائلات الجائعة—وجبات طارئة، ومواد أساسية للأيتام والأرامل، ودعم رمضان.',
      },
    },
    accountsSection: {
      title: 'حسابات التبرع',
      sub:
        'أرسل تبرعك عبر زاد أو إدهب أو التحويل البنكي. انسخ النص والصقه في تطبيق المحفظة أو البنك.',
    },
    accounts: {
      mobileMoney: 'محفظة نقال',
      accountNumber: 'رقم الحساب',
      copy: 'نسخ',
      copied: 'تم النسخ',
    },
    auth: {
      loginTitle: 'تسجيل دخول الإدارة',
      loginSub: 'سجّل الدخول لإدارة التبرعات وطرق الدفع.',
      email: 'البريد الإلكتروني',
      password: 'كلمة المرور',
      signIn: 'تسجيل الدخول',
      signingIn: 'جارٍ تسجيل الدخول…',
      loginError: 'البريد أو كلمة المرور غير صحيحة',
      demoTitle: 'حساب تجريبي',
      demoEmail: 'البريد',
      demoPassword: 'كلمة المرور',
    },
    voting: {
      title: 'صوّت لطريقة الدفع المفضلة',
      sub: 'ساعدنا على معرفة قناة التبرع المفضلة لدى المجتمع. صوت واحد لكل زائر.',
      vote: 'صوّت',
      yourVote: 'صوتك',
      thankYou: 'شكرًا لتصويتك!',
      alreadyVoted: 'لقد صوّتت مسبقًا.',
      error: 'تعذّر تسجيل صوتك. حاول مرة أخرى.',
    },
    projectVoting: {
      title: 'صوّت لمشروع',
      sub: 'أخبرنا أي قضية تهمك أكثر. صوتك يساعدنا على تحديد أولويات مشاريع المجتمع. صوت واحد لكل زائر.',
      vote: 'صوّت لهذا المشروع',
      yourVote: 'اختيارك',
      votes: 'أصوات',
      thankYou: 'شكرًا — تم تسجيل صوتك!',
      alreadyVoted: 'لقد صوّتت لمشروع مسبقًا.',
      error: 'تعذّر تسجيل صوتك. حاول مرة أخرى.',
    },
    contributions: {
      totalsTitle: 'إجماليات المجتمع',
      totalsSub:
        'المساهمات المعتمدة وفق الشروط: الإغاثة الخيرية (صافٍ بعد 5٪ رسوم إدارية) وصندوق المشاريع الرأسمالية الوطنية.',
      totalCharitableNet: 'الإغاثة الخيرية (صافٍ)',
      totalCapitalFund: 'صندوق المشاريع الرأسمالية الوطنية',
      totalCharitableHint: '95٪ من 40٪ الخيرية بعد الرسوم الإدارية',
      totalCapitalHint: '60٪ محجوزة لتخصيص اللجنة',
      totalDonations: 'الإغاثة الخيرية (إجمالي)',
      totalInvestments: 'صندوق المشاريع الرأسمالية الوطنية',
      currencyNote: 'USD',
      formTitle: 'سجّل مساهمتك',
      formSub:
        'أدخل المبلغ عبر Zaad أو Edahab أو تحويل بنكي. وفق الشروط: 40٪ إغاثة خيرية و60٪ للصندوق الرأسمالي الوطني.',
      name: 'الاسم الكامل',
      namePlaceholder: 'اسمك',
      whatsapp: 'واتساب',
      whatsappPlaceholder: '+252 63 0000000',
      totalAmount: 'إجمالي المبلغ المدفوع',
      totalAmountRequired: 'أدخل إجمالي المبلغ الذي دفعته.',
      splitTitle: 'يتقسّم دفعك كالتالي',
      charitableGross: 'الإغاثة الخيرية',
      adminFee: 'الرسوم الإدارية (5٪ من الجزء الخيري)',
      netCharitable: 'صافٍ للبرامج الخيرية',
      capitalFund: 'صندوق المشاريع الرأسمالية الوطنية',
      splitNote:
        'تُودَع أموال الصندوق في حساب ضمان مستقل ويُخصَّص توزيعها بالتصويت الديمقراطي.',
      donationAmount: 'الإغاثة الخيرية',
      investmentAmount: 'صندوق المشاريع الرأسمالية الوطنية',
      charitableProgram: 'تفضيل البرنامج الخيري (40٪)',
      capitalFundSector: 'تفضيل قطاع الصندوق الرأسمالي (60٪)',
      donationProjectVote: 'البرنامج الخيري (40٪)',
      investmentProjectVote: 'قطاع الصندوق الرأسمالي (60٪)',
      selectProject: 'اختر مشروعًا',
      donationProjectRequired: 'اختر برنامجًا خيريًا للجزء 40٪.',
      investmentProjectRequired: 'اختر قطاعًا للصندوق الرأسمالي للجزء 60٪.',
      paymentAccount: 'حساب الدفع',
      selectPaymentAccount: 'اختر حساب الدفع',
      paymentAccountRequired: 'اختر الحساب الذي دفعت إليه.',
      paymentSectionTitle: 'تفاصيل الدفع',
      loadingOptions: 'جارٍ تحميل طرق الدفع والمشاريع…',
      optionsLoadError: 'تعذّر تحميل طرق الدفع أو المشاريع. تحقّق من الاتصال وحاول مرة أخرى.',
      retryLoad: 'حاول مرة أخرى',
      ageConfirm: 'أؤكد أن عمري 18 سنة على الأقل.',
      ageRequired: 'يجب تأكيد أنك تبلغ 18 سنة على الأقل.',
      termsConfirmPrefix: 'لقد قرأت وأوافق على',
      termsConfirmLink: 'الشروط والأحكام',
      termsRequired: 'يجب الموافقة على الشروط والأحكام.',
      refundNotice:
        'التبرعات غير قابلة للاسترداد عادةً. قد تُنظر طلبات الاسترداد خلال 7 أيام للمعاملات المكررة أو الخاطئة.',
      riskDisclaimer:
        'الصندوق الرأسمالي ليس ضمانًا للعائد. الاستثمار ينطوي على مخاطر.',
      formHint:
        'اختيار المشروع يعبّر عن تفضيلك. التخصيص النهائي يتم بالتصويت الديمقراطي تحت إشراف لجنة مستقلة.',
      submit: 'إرسال للمراجعة',
      submitting: 'جارٍ الإرسال…',
      submitSuccess: 'شكرًا! طلبك قيد المراجعة.',
      submitError: 'تعذّر الإرسال. حاول مرة أخرى.',
    },
    admin: {
      title: 'نظام الإدارة',
      welcome: 'مرحبًا',
      backToSite: 'العودة للموقع',
      logout: 'تسجيل الخروج',
      navAria: 'تنقل الإدارة',
      dashboard: 'لوحة التحكم',
      donations: 'التبرعات',
      projects: 'المشاريع',
      methods: 'طرق الدفع',
      loading: 'جارٍ التحميل…',
      totalDonations: 'التبرعات المسجّلة',
      paymentMethods: 'طرق الدفع',
      totalVotes: 'أصوات المجتمع',
      manageDonations: 'إدارة التبرعات',
      manageMethods: 'إدارة الطرق',
      addDonation: 'إضافة تبرع',
      editDonation: 'تعديل تبرع',
      addMethod: 'إضافة طريقة دفع',
      editMethod: 'تعديل طريقة',
      donorName: 'اسم المتبرع',
      amount: 'المبلغ',
      sector: 'مجال التبرع',
      method: 'طريقة الدفع',
      notes: 'ملاحظات',
      date: 'التاريخ',
      actions: 'إجراءات',
      add: 'إضافة',
      save: 'حفظ',
      cancel: 'إلغاء',
      edit: 'تعديل',
      delete: 'حذف',
      confirmDelete: 'حذف هذا السجل؟',
      noDonations: 'لا توجد تبرعات مسجّلة بعد.',
      none: 'لا شيء',
      methodId: 'معرّف الطريقة',
      methodLabel: 'الاسم',
      methodValue: 'الحساب / الرقم',
      hintKey: 'النوع',
      logoFile: 'اسم ملف الشعار',
      active: 'نشط',
      yes: 'نعم',
      no: 'لا',
      votes: 'الأصوات',
      portalTag: 'بوابة الإدارة',
      menuSection: 'القائمة الرئيسية',
      viewSite: 'عرض الموقع العام',
      roleAdmin: 'مسؤول',
      openMenu: 'فتح القائمة',
      closeMenu: 'إغلاق القائمة',
      dashboardDesc: 'نظرة عامة على التبرعات وطرق الدفع وأصوات المجتمع.',
      donationsDesc: 'تسجيل وإدارة التبرعات الواردة عبر جميع مجالات التبرع.',
      methodsDesc: 'إعداد قنوات الدفع المعروضة في صفحة التبرع العامة.',
      donationsList: 'جميع التبرعات',
      methodsList: 'جميع طرق الدفع',
      quickActions: 'إجراءات سريعة',
      manageDonationsDesc: 'إضافة أو تعديل أو حذف سجلات التبرع.',
      manageMethodsDesc: 'تحديث الحسابات والشعارات ونتائج التصويت.',
      manageProjects: 'إدارة المشاريع',
      manageProjectsDesc: 'أضف قضايا يمكن للجمهور التصويت لتحديد أولويتها.',
      addProject: 'إضافة مشروع',
      editProject: 'تعديل مشروع',
      projectTitle: 'عنوان المشروع',
      projectDescription: 'الوصف',
      projectsDesc: 'إدارة مشاريع مجالات التبرع التي يصوّت لها الزوار على الموقع العام.',
      projectsFormHint: 'اختر مجال التبرع، ثم عدّل العنوان والوصف. لكل مجال مشروع واحد.',
      selectSector: 'اختر مجال التبرع',
      projectSectorRequired: 'يرجى اختيار مجال التبرع للمشروع.',
      projectSaveError: 'تعذّر حفظ المشروع. تأكد من تشغيل خادم API.',
      projectsLoadError: 'تعذّر تحميل المشاريع. شغّل npm start لتشغيل API والموقع معًا.',
      saving: 'جارٍ الحفظ…',
      sectorTaken: 'يوجد مشروع مسبقًا لهذا المجال',
      projectsList: 'جميع المشاريع',
      noProjects: 'لا توجد مشاريع بعد. أضف مشروعًا للتصويت عليه.',
      totalProjects: 'المشاريع النشطة',
      projectVotes: 'أصوات المشاريع',
      totalAmount: 'إجمالي المسجّل',
      donationCount: 'التبرعات',
      unspecified: 'غير محدد',
      kpiDonationsSub: 'سجلات في الدفتر',
      kpiProjectsSub: 'مفتوحة للتصويت',
      kpiProjectVotesSub: 'أولويات المجتمع',
      kpiMethodsSub: 'قنوات نشطة',
      kpiMethodVotesSub: 'تفضيلات الدفع',
      chartDonationsBySector: 'التبرعات حسب المجال',
      chartDonationsBySectorSub: 'التوزيع عبر مجالات البرنامج',
      chartDonationsTrend: 'التبرعات عبر الزمن',
      chartDonationsTrendSub: 'الحجم الشهري والمبالغ المسجّلة',
      chartProjectVotes: 'أصوات المشاريع',
      chartProjectVotesSub: 'القضايا التي يُعطيها المجتمع أولوية',
      chartMethodVotes: 'أصوات طرق الدفع',
      chartMethodVotesSub: 'قنوات التبرع المفضّلة',
      noChartData: 'لا توجد بيانات بعد — سيظهر النشاط هنا.',
      dashboardLoadError: 'تعذّر تحميل بيانات لوحة التحكم.',
      recentDonations: 'أحدث التبرعات',
      projectLeaderboard: 'ترتيب تصويت المشاريع',
      methodLeaderboard: 'ترتيب طرق الدفع',
      viewAll: 'عرض الكل',
      contributions: 'الطلبات',
      contributionsDesc: 'مراجعة طلبات التبرع والاستثمار من الجمهور. اعتمدها لإضافتها إلى إجماليات الموقع.',
      submissionsList: 'طلبات المساهمة',
      noSubmissions: 'لا توجد طلبات في هذه الفئة.',
      status: 'الحالة',
      statusPending: 'قيد الانتظار',
      statusApproved: 'معتمد',
      statusRejected: 'مرفوض',
      statusAll: 'الكل',
      approve: 'اعتماد',
      reject: 'رفض',
      pendingSubmissions: 'طلبات قيد الانتظار',
      kpiPendingSub: 'بانتظار المراجعة',
      manageContributions: 'مراجعة الطلبات',
      manageContributionsDesc: 'اعتماد التبرعات والاستثمارات للإجماليات العامة.',
      publicTotalDonations: 'إجمالي التبرعات العام',
      publicTotalInvestments: 'إجمالي الاستثمارات العام',
      submissionReport: 'تقرير الطلبات',
      submissionReportDesc: 'ملخص ومخططات وتصدير كامل لجميع طلبات التبرع والاستثمار.',
      reportLoadError: 'تعذّر تحميل تقرير الطلبات.',
      reportGenerated: 'تاريخ التقرير',
      reportTotalSubmissions: 'إجمالي الطلبات',
      reportDonationSubmitted: 'التبرعات المُرسلة',
      reportInvestmentSubmitted: 'الاستثمارات المُرسلة',
      reportStatusBreakdown: 'توزيع الحالات',
      reportAmountsByStatus: 'المبالغ حسب الحالة',
      reportApproved: 'معتمد',
      reportPending: 'قيد الانتظار',
      reportMonthlyTrend: 'الاتجاه الشهري',
      reportDetailTable: 'تفاصيل الطلبات',
      reportRecords: 'سجل',
      reportReviewed: 'تاريخ المراجعة',
      exportCsv: 'تصدير CSV',
      printReport: 'طباعة التقرير',
    },
    footer: {
      thanks:
        'شكرًا لكرمك. احتفظ بإيصال التحويل لسجلاتك.',
      aboutText:
        'Maalin iyo Habeen منظمة خيرية مسجّلة تعمل في أراضي الصومال—برامج في الصحة النفسية والإغاثة والتنمية المجتمعية.',
      menuHeading: 'تصفح',
      menuAria: 'أقسام الموقع',
      legalHeading: 'قانوني',
      legalAria: 'روابط قانونية',
      contactHeading: 'تواصل',
      socialHeading: 'تابعنا',
      socialAria: 'روابط وسائل التواصل',
      socialFacebook: 'فيسبوك',
      socialInstagram: 'إنستغرام',
      socialX: 'X (تويتر)',
      socialYoutube: 'يوتيوب',
      socialWhatsapp: 'واتساب',
      terms: 'الشروط والأحكام',
      privacy: 'سياسة الخصوصية',
      rights: 'جميع الحقوق محفوظة.',
    },
    legal: {
      backToHome: 'العودة للرئيسية',
    },
    terms: {
      notice:
        'النص القانوني الملزم باللغة الإنجليزية. إذا اختلفت أي ترجمة، يُعتمد النص الإنجليزي.',
    },
    termsGate: {
      title: 'الشروط والأحكام',
      lead:
        'قبل استخدام Maalin iyo Habeen، يرجى قراءة الشروط والأحكام والموافقة عليها. يشمل ذلك تقسيم التبرعات، وحوكمة صندوق المشاريع الرأسمالية الوطنية، وحقوقك كمتبرع أو مستثمر.',
      checkboxPrefix: 'لقد قرأت وأوافق على',
      termsLink: 'الشروط والأحكام',
      accept: 'أوافق وأتابع',
      decline: 'أرفض',
      declinedMessage:
        'يجب الموافقة على الشروط والأحكام لاستخدام هذا الموقع. يرجى مراجعتها والموافقة للمتابعة.',
    },
    privacy: {
      title: 'سياسة الخصوصية',
      notice:
        'النص القانوني الملزم بالإنجليزية في الشروط (القسم 7). إذا اختلفت أي ترجمة، يُعتمد النص الإنجليزي.',
      s71: 'نجمع المعلومات الشخصية (الاسم، بيانات الاتصال، الدفع) لمعالجة التبرعات والتواصل وإدارة التصويت.',
      s72: 'لا نشارك أو نبيع أو نتاجر ببياناتك لأغراض تسويقية.',
      s73intro: 'قد تُشارك معلوماتك مع:',
      s73a: 'معالجات الدفع لإتمام المعاملات',
      s73b: 'اللجنة والمدققين للتحقق',
      s73c: 'السلطات التنظيمية عند الاقتضاء',
      s74: 'نتخذ تدابير فنية وتنظيمية معقولة لحماية بياناتك.',
      s75: 'يحق لك طلب الوصول أو التصحيح أو الحذف عبر info@maaliniyohabeen.org.',
    },
  },
};

export const sectorIds = [
  'madness',
  'disability',
  'funerals',
  'mosquesMadrasas',
  'waterWells',
  'food',
];
